import { useEffect, useRef, useState } from "react";
import { Eye, FileUp } from "lucide-react";
import "./HRReleasingOfferLetterPage.css";
import { getJobs } from "../../services/jobService";
import { releaseManualOfferLetter } from "../../services/offerLetterService";

const BLACKROTH_HQ_LOCATION =
  "12B, BLACKROTH, THE HEADQUARTERS, 6TH FLOOR, ORBIT, KNOWLEDGE CITY, RAIDURG, HYDERABAD, TELANGANA-500019";

const initialFormState = {
  candidateId: "",
  jobTitle: "",
  fullName: "",
  email: "",
  employmentType: "",
  workLocation: "",
  location: "",
  dateOfJoining: "",
  workMode: "",
};

const HRUploadOfferLetterPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [jobOptions, setJobOptions] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [locationError, setLocationError] = useState("");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const locationDropdownRef = useRef(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await getJobs({ limit: 1000, is_active: true });
        const jobs = Array.isArray(response?.data) ? response.data : [];

        setJobOptions(
          jobs
            .filter((job) => job?.is_active)
            .map((job) => job.title)
            .filter(Boolean),
        );
      } catch (error) {
        console.error("Failed to fetch jobs:", error);
        setJobOptions([]);
      }
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target)
      ) {
        setIsLocationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      setSelectedFileName("");
      setFileError("");
      return;
    }

    const isPdfFile =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

    if (!isPdfFile) {
      setSelectedFile(null);
      setSelectedFileName("");
      setFileError("Only PDF files are allowed.");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setSelectedFileName(file.name);
    setFileError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.location.trim()) {
      setLocationError("Please select location.");
      return;
    }

    if (fileError || !selectedFile) {
      setFileError("Only PDF files are allowed.");
      return;
    }

    try {
      setIsSubmitting(true);
      setFileError("");

      await releaseManualOfferLetter({
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        candidateId: formData.candidateId.trim(),
        job: formData.jobTitle.trim(),
        employmentType: formData.employmentType.trim(),
        workLocation: formData.workLocation.trim(),
        location: formData.location.trim(),
        dateOfJoining: formData.dateOfJoining,
        workMode: formData.workMode,
        offerLetter: selectedFile,
      });

      setFormData(initialFormState);
      setSelectedFile(null);
      setSelectedFileName("");
      setLocationError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsPopupOpen(true);
    } catch (error) {
      setFileError(
        error.response?.data?.message || "Failed to upload offer letter.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenPreview = () => {
    if (!previewUrl) {
      return;
    }

    const previewWindow = window.open(previewUrl, "_blank", "noopener");

    if (!previewWindow) {
      setFileError("Preview was blocked by the browser. Please allow popups and try again.");
    }
  };

  const handleClosePopup = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsPopupOpen(false);
  };

  return (
    <article className="hr-page-card offer-letter-page">
      <div className="hr-page-title">
        <FileUp size={20} />
        <h2>Upload Offer Letter</h2>
      </div>

      <form className="offer-letter-form-card" onSubmit={handleSubmit}>
        <div className="offer-letter-form-grid">
          <div className="offer-letter-form-field">
            <label>Candidate ID</label>
            <input
              type="text"
              value={formData.candidateId}
              onChange={(event) => handleChange("candidateId", event.target.value)}
              placeholder="Enter candidate ID"
              required
            />
          </div>

          <div className="offer-letter-form-field">
            <label>Candidate Name</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(event) => handleChange("fullName", event.target.value)}
              placeholder="Enter candidate name"
              required
            />
          </div>

          <div className="offer-letter-form-field">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(event) => handleChange("email", event.target.value)}
              placeholder="Enter candidate email"
              required
            />
          </div>

          <div className="offer-letter-form-field">
            <label>Job Title</label>
            <select
              value={formData.jobTitle}
              onChange={(event) => handleChange("jobTitle", event.target.value)}
              required
            >
              <option value="">Select job title</option>
              {jobOptions.map((jobTitle, index) => (
                <option key={`${jobTitle}-${index}`} value={jobTitle}>
                  {jobTitle}
                </option>
              ))}
            </select>
          </div>

          <div className="offer-letter-form-field">
            <label>Employment Type</label>
            <select
              value={formData.employmentType}
              onChange={(event) => handleChange("employmentType", event.target.value)}
              required
            >
              <option value="">Select employment type</option>
              <option value="FULL_TIME">Full Time</option>
              <option value="INTERN">Intern</option>
            </select>
          </div>


          <div className="offer-letter-form-field">
            <label>Work Location</label>
            <select
              value={formData.workLocation}
              onChange={(event) => handleChange("workLocation", event.target.value)}
              required
            >
              <option value="">Select work location</option>
              <option value="Hyderabad">Hyderabad</option>
            </select>
          </div>


          <div className="offer-letter-form-field">
            <label>On_Boarding Location</label>
            <div
              className="offer-letter-location-dropdown"
              ref={locationDropdownRef}
            >
              <button
                type="button"
                className={`offer-letter-location-select ${
                  isLocationOpen ? "open" : ""
                }`}
                onClick={() => setIsLocationOpen((current) => !current)}
                aria-haspopup="listbox"
                aria-expanded={isLocationOpen}
              >
                <span>
                  {formData.location || "Select On_Boarding Location"}
                </span>
              </button>
              {isLocationOpen && (
                <div className="offer-letter-location-menu" role="listbox">
                  <button
                    type="button"
                    className={`offer-letter-location-option placeholder ${
                      !formData.location ? "selected" : ""
                    }`}
                    onClick={() => {
                      handleChange("location", "");
                      setIsLocationOpen(false);
                    }}
                    role="option"
                    aria-selected={!formData.location}
                  >
                    Select Location
                  </button>
                  <button
                    type="button"
                    className={`offer-letter-location-option ${
                      formData.location === BLACKROTH_HQ_LOCATION ? "selected" : ""
                    }`}
                    onClick={() => {
                      handleChange("location", BLACKROTH_HQ_LOCATION);
                      setLocationError("");
                      setIsLocationOpen(false);
                    }}
                    role="option"
                    aria-selected={formData.location === BLACKROTH_HQ_LOCATION}
                  >
                    {BLACKROTH_HQ_LOCATION}
                  </button>
                </div>
              )}
            </div>
            {locationError && (
              <span className="offer-letter-file-error">{locationError}</span>
            )}
          </div>

          <div className="offer-letter-form-field">
            <label>Date of Joining</label>
            <input
              type="date"
              value={formData.dateOfJoining}
              onChange={(event) => handleChange("dateOfJoining", event.target.value)}
              required
            />
          </div>

          <div className="offer-letter-form-field">
            <label>Work Mode</label>
            <select
              value={formData.workMode}
              onChange={(event) => handleChange("workMode", event.target.value)}
              required
            >
              <option value="">Select work mode</option>
              <option value="WFO/WFH/Hybrid">WFO/WFH/Hybrid</option>
            </select>
          </div>

          <div className="offer-letter-form-field offer-letter-upload-field">
            <label>Upload Offer Letter</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              required
            />
            {fileError && <span className="offer-letter-file-error">{fileError}</span>}
            {selectedFileName && (
              <span className="offer-letter-file-row">
                <span className="offer-letter-file-name">{selectedFileName}</span>
                <button
                  type="button"
                  className="offer-letter-preview-btn"
                  onClick={handleOpenPreview}
                  disabled={!previewUrl}
                >
                  <Eye size={16} />
                  Preview
                </button>
              </span>
            )}
          </div>
        </div>

        <button type="submit" className="offer-letter-submit-btn">
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </form>

      {isPopupOpen && (
        <div className="offer-letter-popup-overlay" onClick={handleClosePopup}>
          <div
            className="offer-letter-popup-card"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Offer letter generated message"
          >
            <h3>Offer Letter Generated</h3>
            <p>Offer letter sent to Candidate successfully.</p>
            <button
              type="button"
              className="offer-letter-popup-btn"
              onClick={handleClosePopup}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

export default HRUploadOfferLetterPage;
